import type { LBNodeData } from '@/proto/logiblocks/logiblocks';
import { CONTROL_FLOW_ICON_COLOR } from '../../styles';
import {
  type GraphData,
  NodeCategory,
  OutputType,
  type LBNodeInfo,
} from '../../types';
import { FilterControlFlowCustomization } from './FilterControlFlowCustomization';
import { defaultNodeData } from '../../graphUtils';
import { FilterIcon } from '@/assets/icons/icons';

export const FilterControlFlowNodeInfo: LBNodeInfo = {
  protobufNodeDataKey: 'filter_control_flow',

  category: NodeCategory.CONTROL_FLOW,

  label: 'Filter',

  hasInput: true,

  getIcon: (size: number) => (
    <FilterIcon width={size} height={size} color={CONTROL_FLOW_ICON_COLOR} />
  ),

  outputType: OutputType.SUBGRAPH_PARENT,

  getSubtitle: (data: LBNodeData, _graph: GraphData) => {
    const fc = data.filter_control_flow;
    if (!fc) return '';

    const arrayFields: Array<[keyof typeof fc, string]> = [
      ['status', 'Progress'],
      ['team', 'Team'],
      ['from_location', 'Origin address'],
      ['to_location', 'Destination address'],
      ['origin_state', 'Origin state'],
      ['destination_state', 'Destination state'],
      ['location_type', 'Location type'],
      ['alert_types', 'Alerts'],
      ['type', 'Risk'],
      ['last_open_alert_aging', 'Last open alert aging'],
      ['delivery_status', 'Timeliness'],
      ['pod_status', 'POD'],
      ['product', 'Product'],
      ['product_type', 'Product type'],
      ['project_id', 'Project ID'],
      ['temperature', 'Temperature'],
      ['courier', 'Transport provider'],
      ['link_code', 'Link code'],
      ['created_at', 'Created at'],
    ];

    const activeFilters: string[] = [];

    for (const [key, label] of arrayFields) {
      const vals = fc[key] as string[] | undefined;
      if (vals?.length) activeFilters.push(label);
    }

    const pickupCount = Object.keys(fc.scheduled_pickup_date ?? {}).length;
    if (pickupCount) activeFilters.push('Pickup date');

    const deliveryCount = Object.keys(fc.scheduled_delivery_date ?? {}).length;
    if (deliveryCount) activeFilters.push('Delivery date');

    if (fc.archived) activeFilters.push('Archived');

    // If no filters
    if (!activeFilters.length) return '';

    // If all filters selected
    const totalPossible = arrayFields.length + 2 + 1; // arrayFields + pickup + delivery + archived
    if (activeFilters.length === totalPossible) return 'All';

    // Show first 2 + remaining count
    const visible = activeFilters.slice(0, 2);
    const remaining = activeFilters.length - visible.length;

    return remaining > 0
      ? `${visible.join(', ')} +${remaining} more`
      : visible.join(', ');
  },

  CustomizationComponent: FilterControlFlowCustomization,

  getSubgraphIndex: (nodeData: LBNodeData) => {
    return nodeData.filter_control_flow?.subgraph_index;
  },

  setSubgraphIndex: (nodeData: LBNodeData, index: number) => {
    if (nodeData.filter_control_flow) {
      nodeData.filter_control_flow.subgraph_index = index;
    }
  },

  createDefaultNodeData: () =>
    defaultNodeData('filter_control_flow', {
      subgraph_index: -1,
      team: [],
      team_logic: '',
      from_location: [],
      to_location: [],
      origin_state: [],
      destination_state: [],
      location_type: [],
      location_type_logic: '',
      alert_types: [],
      alert_types_logic: '',
      type: [],
      last_open_alert_aging: [],
      status: [],
      delivery_status: [],
      pod_status: [],
      product: [],
      product_logic: '',
      product_type: [],
      product_type_logic: '',
      project_id: [],
      project_id_logic: '',
      temperature: [],
      courier: [],
      link_code: [],
      scheduled_pickup_date: {},
      scheduled_delivery_date: {},
      created_at: {},
      archived: '',
    }),
};
